/**
 * Purpose:
 *   Show a one-time permission warning after the proxy redirects a user to a forbidden URL.
 *
 * Notes:
 *   - i18n: `common` namespace (toast.forbidden_access).
 *   - Removes only the `forbidden` query parameter after the toast is queued.
 */

'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/toast/hooks/useToast';

export function ForbiddenAccessToast() {
  const { addToast } = useToast();
  const { t } = useTranslation('common');
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const handledRef = useRef(false);
  const isForbidden = searchParams.get('forbidden') === '1';

  useEffect(() => {
    if (!isForbidden) {
      handledRef.current = false;
      return;
    }

    if (handledRef.current) {
      return;
    }

    handledRef.current = true;
    addToast({
      type: 'warning',
      message: t('toast.forbidden_access'),
    });

    const nextSearchParams = new URLSearchParams(searchParams.toString());
    nextSearchParams.delete('forbidden');
    const query = nextSearchParams.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  }, [addToast, isForbidden, pathname, router, searchParams, t]);

  return null;
}
